import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, of, tap } from 'rxjs';
import { AnalyticsInfo, AnalyticsRangeContext } from '@services/analytics-services/core';
import { DateRangePresetType } from '@services/core-services/models';
import { PaymentAnalyticsService, PaymentStats } from '@services/analytics-services/payment';
import { DashboardStore } from '../dashboard/dashboard.store';
import { ChartOptions } from '@services/analytics-services/hight-chart-configurations/high-chart-config';
import { STATUS_COLORS } from '@services/analytics-services/hight-chart-configurations';
import { ErrorHandlerService } from '@services/core-services/error-handler.service';
import { ProcessingState } from '@services/core-services/models';

@Injectable()
export class DashboardPaymentsStore {
  private paymentAnalytics = new BehaviorSubject<AnalyticsInfo<PaymentStats> | undefined>(
    undefined
  );
  private paymentMethodDistribution = new BehaviorSubject<any[]>([]);
  private paymentMethodChartOptions = new BehaviorSubject<ChartOptions>({});
  private errorMessage = new BehaviorSubject<string>('');
  private loader = new BehaviorSubject<ProcessingState>(ProcessingState.Loading);
  private totalPaymentError = new BehaviorSubject<string | false>(false);
  private paymentError = new BehaviorSubject<string | false>(false);
  private refundError = new BehaviorSubject<string | false>(false);
  private paymentMethodError = new BehaviorSubject<string | false>(false);

  constructor(
    private readonly dashboardStore: DashboardStore,
    private readonly paymentAnalyticsService: PaymentAnalyticsService,
    private readonly errorHandlerService: ErrorHandlerService
  ) {
    // Initialize payment analytics by subscribing to range context changes
    this.dashboardStore.getRangeContext().subscribe(context => {
      this.fetchPaymentAnalytics(context);
    });
  }

  getPaymentAnalytics(): Observable<AnalyticsInfo<PaymentStats> | undefined> {
    return this.paymentAnalytics.asObservable();
  }

  gettotalPaymentError = (): Observable<string | false> => this.totalPaymentError.asObservable();
  getPaymentError = (): Observable<string | false> => this.paymentError.asObservable();
  getRefundError = (): Observable<string | false> => this.refundError.asObservable();
  getPaymentMethodError = (): Observable<string | false> => this.paymentMethodError.asObservable();

  getPaymentMethodDistribution(): Observable<any[]> {
    return this.paymentMethodDistribution.asObservable();
  }

  getPaymentMethodChartOptions(): Observable<ChartOptions> {
    return this.paymentMethodChartOptions.asObservable();
  }

  getErrorMessage(): Observable<string> {
    return this.errorMessage.asObservable();
  }

  private resetErrors(): void {
    this.totalPaymentError.next(false);
    this.paymentError.next(false);
    this.refundError.next(false);
    this.paymentMethodError.next(false);
    this.errorMessage.next('');
  }

  loadPaymentAnalytics(
    context?: AnalyticsRangeContext
  ): Observable<AnalyticsInfo<PaymentStats> | undefined> {
    this.loader.next(ProcessingState.Loading);
    this.totalPaymentError.next(false);
    this.paymentError.next(false);
    this.refundError.next(false);

    const ctx = context || this.dashboardStore.getRangeContextSnapshot();
    const { range, compare, interval, preset } = ctx;

    return this.paymentAnalyticsService
      .getAnalytics({
        preset: preset as DateRangePresetType,
        range,
        compare,
        interval,
      })
      .pipe(
        tap(analytics => {
          this.paymentAnalytics.next(analytics);
          this.loader.next(ProcessingState.Success);
        }),
        catchError(error => {
          this.loader.next(ProcessingState.Error);
          const errorMessage = this.errorHandlerService.handleServerError(error);
          const errorString =
            typeof errorMessage === 'string' ? errorMessage : 'Error loading payment analytics';
          this.errorMessage.next(errorString);

          this.totalPaymentError.next(errorString);
          this.paymentError.next(errorString);
          this.refundError.next(errorString);

          this.paymentAnalytics.next(undefined);
          return of(undefined);
        })
      );
  }

  loadPaymentMethodDistribution(context?: AnalyticsRangeContext): Observable<any[]> {
    this.loader.next(ProcessingState.Loading);
    this.paymentMethodError.next(false);

    const ctx = context || this.dashboardStore.getRangeContextSnapshot();
    return this.paymentAnalyticsService.getPaymentMethodDistribution(ctx).pipe(
      map((response: unknown) => response as { list: any[] }),
      tap((response: { list: any[] }) => {
        if (!response || !response.list || response.list.length === 0) {
          this.paymentMethodDistribution.next([]);
          this.paymentMethodChartOptions.next({
            data: [],
            title: 'Payment Method Distribution',
          });
          this.loader.next(ProcessingState.Empty);
          return;
        }

        const chartData = response.list.map((item: any, index: number) => ({
          label: item.payment_method || 'Unknown',
          y: Number(item.payment_count) || 0,
          color:
            Object.values(STATUS_COLORS)[index % Object.values(STATUS_COLORS).length] ||
            STATUS_COLORS['neutral'],
          originalAmount: Number(item.amount) || 0,
        }));

        this.paymentMethodDistribution.next(response.list);

        const totalCount = chartData.reduce((sum: number, item: any) => sum + item.y, 0);

        const options: ChartOptions = {
          data: chartData,
          title: 'Payment Method Distribution',
          showLegend: true,
          legend: {
            enabled: true,
            align: 'center',
            verticalAlign: 'bottom',
            layout: 'horizontal',
          },
          donut: true,
          tooltipFormatter: (point: Highcharts.Point): string => {
            const pointName = point.name || 'Unknown';
            const pointY = point.y;
            const pointPercent = point.percentage ? point.percentage.toFixed(1) : '0.0';
            const pointColor = point.color;

            return `
              <div class="custom-chart-tooltip" style="padding:12px;">
                <div class="d-flex flex-column spacing-8 align-items-start w-100">
                  <h4 class="h4-bold primary-0 text-capitalize" style="margin: 0">${pointName}</h4>
                  <span class="dash-boarder w-100"></span>
                  <div class="d-flex align-items-center justify-content-between spacing-8 w-100">
                    <div class="d-flex align-items-center spacing-4 w-100 text-center">
                      <div style="background:${pointColor}" class="tooltipDot d-flex align-items-center h4-bold">&nbsp;</div>
                      <div class="h4-reg primary-50">Count (% Total)</div>
                    </div>
                    <div class="h4-bold primary-0">${pointY} (${pointPercent}%)</div>
                  </div>
                </div>
              </div>
            `;
          },
          donutOptions: {
            centerText: {
              text: `${totalCount.toLocaleString()}`,
              subtext: 'Total Payments',
            },
          },
        };

        this.paymentMethodChartOptions.next(options);
        this.loader.next(ProcessingState.Success);
      }),
      catchError(error => {
        this.loader.next(ProcessingState.Error);
        const errorMessage = this.errorHandlerService.handleServerError(error);
        const errorString =
          typeof errorMessage === 'string' ? errorMessage : 'Error loading payment methods';
        this.errorMessage.next(errorString);
        this.paymentMethodError.next(errorString);

        this.paymentMethodChartOptions.next({
          data: [],
          title: 'Payment Method Distribution',
        });
        this.paymentMethodDistribution.next([]);
        return of([]);
      }),
      map(response => {
        if (response && 'list' in response) {
          return response.list;
        }
        return [];
      })
    );
  }

  fetchPaymentAnalytics(context: AnalyticsRangeContext): void {
    // These HTTP observables typically complete automatically, so storing
    // the subscription isn't strictly necessary for leak prevention here.
    this.loadPaymentAnalytics(context).subscribe();
    this.loadPaymentMethodDistribution(context).subscribe();
  }
}
